import { useCallback, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { Body, H1 } from "@datapay-by-datacom/design-system";
import {
  CompanyContextStep,
  useUnsavedProgressNavigationGuard,
} from "../../onboarding";
import type {
  CompanyContext,
  CompanyContextChange,
} from "../../onboarding";
import { UnsavedProgressConfirmModal } from "../../onboarding/components";
import { useSplitCostingClusteredReportEnabled } from "../hooks/useSplitCostingClusteredReportEnabled";
import { useSplitCostingImportEnabled } from "../hooks/useSplitCostingImportEnabled";
import { SplitCostingWizardProvider } from "../components/SplitCostingWizardProvider";
import { SplitCostingWizardStepper } from "../components/SplitCostingWizardStepper";
import { SplitCostingImportDropzone } from "../components/SplitCostingImportDropzone";
import type { MyPayEnvironment } from "../../generated/core-api";
import s from "./SplitCostingImportLayout.module.css";

const BASE_PATH = "/onboarding/split-costing-import";

function normalizeRoutePath(pathname: string) {
  return pathname.replace(/\/+$/, "").toLowerCase() || "/";
}

/**
 * Route guard + wizard shell for the split costing import flow.
 *
 * Mirrors `PayComponentImportLayout` — company context is gated inline rather
 * than on a separate route. The `CompanyContextStep` gate is shown on the
 * upload step (step 0) and hidden once the user advances to later steps.
 * A disabled dropzone placeholder is shown until a company code is validated.
 *
 * Nested inside `OnboardingLayout`, which provides the `<WorkspaceScreen wide>`
 * shell and the `enable-onboarding` gate — this layout only adds the second
 * flag gate (`enable-split-costing-import`) and the wizard chrome.
 */
export function SplitCostingImportLayout() {
  const { isLoaded, areFlagsOn } = useSplitCostingImportEnabled();
  const singlePageEnabled = useSplitCostingClusteredReportEnabled();
  const [boundContext, setBoundContext] = useState<CompanyContext | null>(null);
  const [companyContextLocked, setCompanyContextLocked] = useState(false);
  const [hasUncommittedProgress, setHasUncommittedProgress] = useState(false);
  const [pendingContextChange, setPendingContextChange] = useState<
    (() => void) | null
  >(null);
  const navigationGuard = useUnsavedProgressNavigationGuard(
    singlePageEnabled && hasUncommittedProgress
  );
  const { pathname } = useLocation();
  const normalizedPathname = normalizeRoutePath(pathname);
  const confirmationMode = navigationGuard.isBlocked
    ? "navigation"
    : pendingContextChange !== null
      ? "context"
      : null;

  const showWizard = boundContext !== null;
  // Show the gate on the upload step (root) so users can see (and re-validate)
  // the company they are importing for; hide it once they advance.
  // Note: no explicit /upload alias route is registered — the index route serves
  // the upload step, so BASE_PATH and BASE_PATH/ are the only paths to match.
  const isUploadStep = normalizedPathname === BASE_PATH;
  const showCompanyGate = singlePageEnabled || !showWizard || isUploadStep;
  const isLegacyChildPath =
    normalizedPathname === `${BASE_PATH}/validate` ||
    normalizedPathname === `${BASE_PATH}/result`;
  const handleContextChangeRequest = useCallback(
    (_change: CompanyContextChange, applyChange: () => void) => {
      if (singlePageEnabled && hasUncommittedProgress) {
        setPendingContextChange(() => applyChange);
        return;
      }
      applyChange();
    },
    [hasUncommittedProgress, singlePageEnabled]
  );

  if (!isLoaded) {
    return null;
  }

  if (!areFlagsOn) {
    return <Navigate to="/" replace />;
  }

  if (singlePageEnabled && !showWizard && isLegacyChildPath) {
    return <Navigate to={BASE_PATH} replace />;
  }

  return (
    <div className={s.container}>
      <header className={s.pageHeader}>
        <H1>Split Costing Import</H1>
        <Body className={s.pageSubtitle}>
          Upload a CSV file to bulk-import split costing for a company. Rows
          are validated before committing — you can review any issues before
          confirming.
        </Body>
      </header>

      {showCompanyGate && (
        <CompanyContextStep
          disabled={singlePageEnabled && companyContextLocked}
          onContextChangeRequest={handleContextChangeRequest}
          onContextBound={setBoundContext}
          onContextCleared={() => setBoundContext(null)}
        />
      )}

      {!showWizard && (
        <SplitCostingImportDropzone isDisabled={true} />
      )}

      {showWizard && (
        <div className={s.wizard}>
          {!singlePageEnabled && <SplitCostingWizardStepper />}
          <SplitCostingWizardProvider
            key={`${boundContext.companyCode}:${boundContext.mypayEnvironment}`}
            companyCode={boundContext.companyCode}
            mypayEnvironment={boundContext.mypayEnvironment as MyPayEnvironment}
            companyName={boundContext.companyName}
            onBusyChange={
              singlePageEnabled ? setCompanyContextLocked : undefined
            }
            onUncommittedProgressChange={
              singlePageEnabled ? setHasUncommittedProgress : undefined
            }
            isParentModalOpen={confirmationMode !== null}
          />
        </div>
      )}

      <UnsavedProgressConfirmModal
        isOpen={confirmationMode !== null}
        title={
          confirmationMode === "context"
            ? "Change company context?"
            : "Leave split costing import?"
        }
        message="The current upload attempt, parsed rows, edits, and validation progress will be lost."
        onStay={() => {
          if (confirmationMode === "navigation") {
            navigationGuard.stay();
            return;
          }
          setPendingContextChange(null);
        }}
        onLeave={() => {
          if (confirmationMode === "navigation") {
            navigationGuard.leaveAnyway();
            return;
          }
          const applyChange = pendingContextChange;
          setPendingContextChange(null);
          applyChange?.();
        }}
      />
    </div>
  );
}
